/**
 * TrendingStrip — Horizontal scrollable trending tokens row.
 *
 * Shows ranked tokens with name, symbol, price, and 24h change.
 * CSS scroll-snap for mobile touch momentum. Skeleton loading state.
 *
 * Story 3.5 — Task 2
 */

import type { TrendingToken } from "@repo/shared-types";
import { Link } from "@tanstack/react-router";

import { resolveApiAssetUrl } from "../../lib/api";
import { cn } from "../../lib/cn";

type TrendingStripProps = {
	tokens: ReadonlyArray<TrendingToken>;
	isLoading: boolean;
};

function formatPrice(price: number): string {
	if (price >= 1) return `$${price.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
	if (price >= 0.01) return `$${price.toFixed(4)}`;
	return `$${price.toFixed(6)}`;
}

function formatChange(change: number): { text: string; className: string } {
	if (change > 0) return { text: `+${change.toFixed(1)}%`, className: "text-green-600" };
	if (change < 0) return { text: `${change.toFixed(1)}%`, className: "text-red-500" };
	return { text: "0.0%", className: "text-pastel-gray" };
}

function SkeletonPill() {
	return (
		<div className="flex-shrink-0 w-48 rounded-xl border border-pastel-border bg-white p-3 animate-pulse">
			<div className="flex items-center gap-2 mb-2">
				<div className="h-5 w-5 rounded-full bg-pastel-light-gray" />
				<div className="h-4 w-20 rounded bg-pastel-light-gray" />
			</div>
			<div className="flex items-center justify-between">
				<div className="h-4 w-16 rounded bg-pastel-light-gray" />
				<div className="h-3 w-12 rounded bg-pastel-light-gray" />
			</div>
		</div>
	);
}

export function TrendingStrip({ tokens, isLoading }: TrendingStripProps) {
	if (!isLoading && tokens.length === 0) {
		return null;
	}

	return (
		<section className="mb-6" aria-label="Trending tokens">
			<h2 className="text-lg font-semibold text-pastel-black mb-3">🔥 Trending</h2>
			<div
				className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide"
				style={{ WebkitOverflowScrolling: "touch" }}
			>
				{isLoading
					? Array.from({ length: 6 }, (_, i) => <SkeletonPill key={`skeleton-${String(i)}`} />)
					: tokens.map((token) => {
							const change = formatChange(token.priceChange24h);
							return (
								<Link
									key={token.contractAddress}
									to="/token/$tokenId"
									params={{ tokenId: token.contractAddress }}
									className="flex-shrink-0 w-48 snap-start rounded-xl border border-pastel-border bg-white p-3 cursor-pointer transition-colors hover:border-pastel-vibrant-blue/30"
								>
									<div className="flex items-center gap-2 mb-2">
										<span className="text-xs font-bold text-pastel-vibrant-blue">
											#{token.rank}
										</span>
										{token.logoUrl ? (
											<img
												src={resolveApiAssetUrl(token.logoUrl) ?? undefined}
												alt={`${token.name} logo`}
												className="h-5 w-5 rounded-full object-cover"
											/>
										) : (
											<div className="h-5 w-5 rounded-full bg-pastel-light-gray flex items-center justify-center">
												<span className="text-[10px] font-bold text-pastel-gray">
													{token.symbol.slice(0, 2)}
												</span>
											</div>
										)}
										<span className="text-sm font-medium text-pastel-black truncate">
											{token.name}
										</span>
										<span className="text-xs text-pastel-gray">{token.symbol}</span>
									</div>
									<div className="flex items-center justify-between">
										<span className="text-sm font-semibold text-pastel-black">
											{formatPrice(token.price)}
										</span>
										<span className={cn("text-xs font-medium", change.className)}>
											{change.text}
										</span>
									</div>
								</Link>
							);
						})}
			</div>
		</section>
	);
}
